import { Alert, Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
  OrderResponseDto,
} from '@planning/app/api'
import { InitializationWrapper } from '@planning/components'
import { railVisitService } from '@planning/index'
import { useWrappedLocalPagination } from '@planning/stores'
import { useTranslate } from '@tolgee/react'
import { GroupMap, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { GeneralCargoOrderLocationItem } from '../GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { OrderListUploadDialogV2 } from '../VesselVisit/Components/Upload/OrderListUploadDialog'
import { VisitGeneralCargoTable } from '../Visits/Components/organisms/VisitGeneralCargoTable'
import { RailVisitOrderListHeader } from './Components/Dashboard/RailVisitOrderListHeader'
import { DuplicatedRailcarAlerts } from './Components/Details/DuplicateRailcarsAlert'
import { AssignRailTracksDialog } from './Components/Dialogs/RailTrackAssignmentDialog'
import { RailVisitClosedWarning } from './Components/Molecules/RailVisitClosedWarning'
import { RailVisitViewToggleButtons } from './Components/Molecules/RailVisitViewToggleButton'
import { RailVisitTableOrderItem } from './Components/Table/RailVisitTableOrderItem'
import { RailVisitTableViewStore } from './Components/Table/RailVisitTableViewStore'
import {
  OperationStartedAlert,
  orderFilterDelegate,
  orderSortingDelegate,
  useContainerYardOperationStore,
} from './RailVisitDetailsPage'
import { RailcarPlanning } from './RailcarPlanning/RailcarPlanning'

export const checkIfSequenceHasErrors = (items: GroupMap): boolean => {
  for (const itemId in items) {
    const item = items[itemId]
    const sequenceValues = Object.values(item.sequence)
    const hasInvalidValues = sequenceValues.some(value => value < 0)
    const hasDuplicates = new Set(sequenceValues).size !== sequenceValues.length

    if (hasInvalidValues || hasDuplicates) return true

    const sortedValues = [...sequenceValues].sort((a, b) => a - b)
    for (let i = 1; i < sortedValues.length; i++) {
      if (sortedValues[i] !== sortedValues[i - 1] + 1) return true
    }
  }
  return false
}

interface Props {
  onOpenGeneralCargoReservation?: (order: OrderResponseDto) => void
  orderLocationsById?: Record<number, GeneralCargoOrderLocationItem[]>
  orderReservationsById?: Record<number, GeneralCargoOrderLocationItem[]>
}

export const RailVisitDischargePage = observer(
  ({ onOpenGeneralCargoReservation, orderLocationsById, orderReservationsById }: Props) => {
    const { id } = useParams()
    const railVisitId = Number(id)
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const {
      appViewStore,
      railVisitItemStore,
      railVisitQueryService,
      orderItemStore,
      containerItemStore,
      generalCargoViewStore,
      dialogStore,
      orderListUploadViewStoreV2,
    } = usePlanningStore()

    const [viewListMode, setViewListMode] = useState(true)
    const [isAssignTrackOpen, setIsAssignTrackOpen] = useState(false)
    const containerYardOperationViewStore = useContainerYardOperationStore()

    useAsyncFetch(async () => {
      if (!railVisitId) return
      await railVisitQueryService.fetchById(railVisitId)
      await orderItemStore.fetchByVisitId(railVisitId)
      await containerItemStore.fetchByVisitId(railVisitId)
    }, [railVisitId, railVisitQueryService, orderItemStore, containerItemStore])

    const railVisitItem = computed(() => railVisitItemStore.elements[railVisitId]).get()
    const isGeneralCargoVisit = railVisitItem?.data.cargoType === CargoType.GeneralCargo

    const dischargeOrdersListStore = useWrappedLocalPagination(
      () =>
        railVisitItem && isGeneralCargoVisit
          ? railVisitItem.discharge.generalCargoOrders
          : railVisitItem?.discharge.containerOrders ?? [],
      undefined,
      orderSortingDelegate,
      orderFilterDelegate,
    )

    const tableStore = useMemo(
      () => new RailVisitTableViewStore(railVisitItem, 'Inbound'),
      [railVisitItem],
    )

    const onUploadClick = async () => {
      orderListUploadViewStoreV2.reset()

      await orderListUploadViewStoreV2.setRailVisitId(railVisitId)
      await orderListUploadViewStoreV2.setDirection(CarrierVisitDirection.Inbound)

      orderListUploadViewStoreV2.setOpen(true)
    }

    const toggleActionBtn = (
      <RailVisitViewToggleButtons
        currentView={viewListMode ? 'list' : 'plan'}
        onListClick={() => setViewListMode(true)}
        onPlanClick={() => setViewListMode(false)}
      />
    )

    const handleAssignRailTrack = async (
      selectedTrackIds: string[],
      targetTrackId?: string | null,
    ) => {
      try {
        appViewStore.showLoader()

        const currentVisit = railVisitItemStore.getById(railVisitId)

        if (!currentVisit) return

        await railVisitService.put({
          ...currentVisit.data,
          railTrackIds: selectedTrackIds.map(id => id),
          reassignmentTargetTrackId: targetTrackId ?? null,
        })

        await railVisitQueryService.fetchById(railVisitId)

        appViewStore.setShowAlert('success', t('tracksUpdated', 'Rail tracks updated successfully'))
      } catch (e) {
        console.error(e)
        appViewStore.setShowAlert('error', t('errorUpdatingTracks', 'Error updating tracks'))
      } finally {
        appViewStore.hideLoader()
      }
    }

    return (
      <InitializationWrapper isInitializing={!railVisitItem}>
        <Box sx={{ backgroundColor: theme.palette.grey[200], minHeight: '100vh' }}>
          <RailVisitOrderListHeader
            railVisitItem={railVisitItem}
            viewMode={viewListMode ? 'list' : 'plan'}
            direction='discharge'
            onUploadClick={onUploadClick}
            onAssignRailTrack={() => setIsAssignTrackOpen(true)}
          />

          <Box>
            {!isGeneralCargoVisit &&
              (viewListMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', p: 3 }}>
                  <DuplicatedRailcarAlerts direction='Inbound' railVisitItem={railVisitItem} />

                  {railVisitItem?.data.isInboundRailcarLengthConflicted && (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                      {t(
                        'railcarLengthHigherThanTrackLength',
                        'Total length of railcars allocated...',
                      )}
                    </Alert>
                  )}

                  <RailVisitTableOrderItem
                    containerYardOperationViewStore={containerYardOperationViewStore!}
                    tableStore={tableStore}
                    renderTableCustomActions={() => (
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                        {toggleActionBtn}
                      </Box>
                    )}
                  />
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      p: '0px 16px',
                    }}
                  >
                    {railVisitItem?.hasOperationStarted && (
                      <Box flex={1}>
                        <OperationStartedAlert />
                      </Box>
                    )}
                    {railVisitItem?.data.status === CarrierVisitStatus.Closed && (
                      <Box flex={1}>
                        <RailVisitClosedWarning />
                      </Box>
                    )}
                    {toggleActionBtn}
                  </Box>
                  <Box sx={{ height: 'calc(100vh - 190px)' }}>
                    <RailcarPlanning
                      railVisitItem={railVisitItem}
                      direction={CarrierVisitDirection.Inbound}
                    />
                  </Box>
                </Box>
              ))}

            {isGeneralCargoVisit && (
              <Box padding={theme.customSpacing.m}>
                <VisitGeneralCargoTable
                  orders={[...dischargeOrdersListStore.pageItems]}
                  carrierVisit={railVisitItem}
                  carrierVisitType={CarrierType.Train}
                  direction={CarrierVisitDirection.Inbound}
                  onOpenGeneralCargoReservation={onOpenGeneralCargoReservation}
                  orderLocationsById={orderLocationsById}
                  orderReservationsById={orderReservationsById}
                />
              </Box>
            )}
          </Box>

          <OrderListUploadDialogV2
            enableFieldSelectionFromOrdersView={false}
            open={orderListUploadViewStoreV2.open}
            handleDialogClose={() => {
              orderListUploadViewStoreV2.setOpen(false)
            }}
            visitType={CarrierType.Train}
          />

          <AssignRailTracksDialog
            open={isAssignTrackOpen}
            onClose={() => setIsAssignTrackOpen(false)}
            railVisitId={railVisitId}
            onSave={handleAssignRailTrack}
          />
        </Box>
      </InitializationWrapper>
    )
  },
)
